// Minimal rich-text editor used for event descriptions.
//
// - Built on TipTap (ProseMirror under the hood) with the StarterKit
//   extensions trimmed to what we actually want: bold, italic, lists, links.
// - Emits HTML via onChange; consumers should sanitize before rendering
//   (see utils/sanitizeHtml.ts).
// - The editor schema constrains what nodes/marks can exist, so the HTML
//   output is already shaped — sanitization is defense-in-depth.

import { useEffect, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import {
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
} from "lucide-react";

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
}

export function RichTextEditor({ value, onChange, placeholder, className }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        // Keep paragraph, bold, italic, strike, bulletList, orderedList,
        // listItem, hardBreak, history.
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
        HTMLAttributes: {
          rel: "noopener noreferrer nofollow",
          target: "_blank",
        },
      }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class: `prose prose-sm sm:prose-base dark:prose-invert max-w-none focus:outline-none min-h-[8rem] ${className ?? ""}`,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // TipTap returns "<p></p>" when empty — normalize to empty string so
      // optional-empty checks in the form stay simple.
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  // Keep editor content in sync if `value` changes externally (e.g., reset
  // after a successful submit).
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() === value) return;
    editor.commands.setContent(value || "", { emitUpdate: false });
  }, [editor, value]);

  if (!editor) return null;

  return (
    <div>
      <Toolbar editor={editor} />
      <div className="relative">
        <EditorContent editor={editor} />
        {editor.isEmpty && placeholder && (
          <div className="pointer-events-none absolute top-0 left-0 text-neutral-400/70 dark:text-neutral-500/70">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

type EditorInstance = NonNullable<ReturnType<typeof useEditor>>;

function Toolbar({ editor }: { editor: EditorInstance }) {
  const [, force] = useState(0);
  // The editor state isn't React-reactive on its own; force a small re-render
  // when selection/marks change so toolbar buttons reflect the active state.
  useEffect(() => {
    const tick = () => force((n) => n + 1);
    editor.on("selectionUpdate", tick);
    editor.on("transaction", tick);
    return () => {
      editor.off("selectionUpdate", tick);
      editor.off("transaction", tick);
    };
  }, [editor]);

  const onLink = () => {
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("URL", prev ?? "https://");
    if (url === null) return; // cancelled
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url })
      .run();
  };

  return (
    <div className="flex items-center gap-1 mb-2 pb-2 border-b border-black/10 dark:border-white/10">
      <ToolbarButton
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
        aria-label="Bold"
      >
        <Bold className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        aria-label="Italic"
      >
        <Italic className="w-4 h-4" />
      </ToolbarButton>
      <div className="w-px h-5 bg-black/10 dark:bg-white/10 mx-1" />
      <ToolbarButton
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        aria-label="Bullet list"
      >
        <List className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        aria-label="Numbered list"
      >
        <ListOrdered className="w-4 h-4" />
      </ToolbarButton>
      <div className="w-px h-5 bg-black/10 dark:bg-white/10 mx-1" />
      <ToolbarButton
        active={editor.isActive("link")}
        onClick={onLink}
        aria-label="Link"
      >
        <LinkIcon className="w-4 h-4" />
      </ToolbarButton>
    </div>
  );
}

function ToolbarButton({
  active,
  onClick,
  children,
  ...rest
}: {
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        w-8 h-8 inline-flex items-center justify-center rounded-md transition-colors
        ${active
          ? "bg-primary-500/15 text-primary-500"
          : "text-neutral-500 dark:text-neutral-400 hover:bg-base-200 hover:text-neutral-700 dark:hover:text-neutral-200"}
      `}
      {...rest}
    >
      {children}
    </button>
  );
}
